/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([], function() {

	const AUTHOR_DASHBOARD_CAPABILITY = 'canAuthorDashboard';

	const APPLICATION_CAPABILITIES_MAP = {
		'dashboard': AUTHOR_DASHBOARD_CAPABILITY,
		'story': AUTHOR_DASHBOARD_CAPABILITY
	};

	const CAPABILITY_NAME_INDEX_MAP = {
		'canAuthorDashboard': 0
	};

	const ASSET_TYPES_SUPPORT_CAPABILITIES = [ 'package' ];
	const MAX_CACHE_ENTRIES = 100;

	// Not facing customer errors, no need to translate
	const SAFETY_CHECK_ERRORS = {
		missingAssetId: 'Missing assetId in the argument sources at element: ',
		unsupportedApplication: 'Application name is not supported by the capabilities service: ',
		unsupportedCapability: 'Capability name is not currently supported by the capabilities service: ',
		notAvailableCapability: 'Not able to get capability for asset id: '
	};

	/**
	*  Capabilities service is used in dashboard/exploration/assistant environment to share the capabilities information
	*  of data sources across these environemnts during a user session in order to reduce the CM query calls that can impact the performance
	*/
	class CapabilitiesService {

		constructor(options) {
			// this option argument is used for unit test
			this._maxCacheEntries = options && options.maxCacheEntries || MAX_CACHE_ENTRIES;
		}

		initialize(glassContext) {
			this._glassContext = glassContext;
			this._ajaxService = glassContext.getCoreSvc('.Ajax');
			this._capabilitiesCache = {};
			// this array is used to purge the oldest entry in the cache when the cache max entries is reached
			this._assetIds = [];
			return Promise.resolve();
		}

		destroy() {
			this._capabilitiesCache = {};
			this._assetIds = [];
			this._glassContext = null;
			this._ajaxService = null;
		}

		/**
		 * @param {string} assetId - asset id of data source
		 * @param {boolean[]} capabilityValues - array of capability values (false means "deny" and true means "grant")
		 * The first value is for 'canAuthorDashboard' value
		 * The second value is for 'canUseExploration' value
		 * Example: _addCapabilityValues('id1', [ false, true ] means
		 * the asset with id='id1' has
		 *		'canAuthorDashboard': 'deny',
		 *		'canUseExploration': 'grant'
		 * });
		 */
		_addCapabilityValues(assetId, capabilityValues) {
			if (this._assetIds.length === this._maxCacheEntries) {
				const assetIdToRemove = this._assetIds.shift();
				delete this._capabilitiesCache[assetIdToRemove];
			}
			this._capabilitiesCache[assetId] = capabilityValues;
			this._assetIds.push(assetId);
		}
		/**
		 * @param {object} spec - dashboard (or fragment) spec in json format
		 * @param {object} spec.dataSources - dataSources property of the spec
		 * @param {object[]} spec.dataSources.sources - array of sources
		 * @param {string} spec.dataSources.sources[i].assetId - asset id of the source element
		 * @param {string} spec.dataSources.sources[i].type - asset type of the source element
		 * @param {string} capabilityName - if not provided, capability is determined based on the application name
		 * @returns {Promise<boolean>} promise that resolves true if all sources in the spec have authoring capability, false otherwise; and rejects for unexpected errors
		 */
		checkAuthoringCapability( { spec }, capabilityName = null) {
			if (!spec) {
				return Promise.resolve(true);
			}
			try {
				const sources = this._getSourceInfosFromSpec(spec);
				return this.hasCapability(sources, capabilityName);
			} catch(error) {
				return Promise.reject(error);
			}
		}

		/**
		 * @returns {string} the authoring capability name of the current application
		 * Ex: 
		 * return "canAuthorDashboard" if the current application is "Dashboard" or "Story"
		 * return "canUseExploration" if the current application is "Explore"
		 * return undefined if the current application is not supported by the service
		 */
		getCurrentAuthoringCapability() {
			const applicationName = this._getApplicationName();
			const capabilityName = applicationName && APPLICATION_CAPABILITIES_MAP[applicationName.toLowerCase()];
			if (!capabilityName) {
				this._glassContext.getCoreSvc('.Logger').warn(SAFETY_CHECK_ERRORS['unsupportedApplication'] + applicationName);
			}
			return capabilityName;
		}

		_getApplicationName() {
			return this._glassContext.currentPerspective.perspective;
		}

		_getSourceInfosFromSpec(spec) {
			let sourceInfos = [];
			const sources = (spec.dataSources && spec.dataSources.sources) || [];
			for (let index = 0; index < sources.length; index++) {
				const source = sources[index];
				sourceInfos.push( {
					assetId: source.assetId,
					type: source.type
				});
			}
			return sourceInfos;
		}

		/**
		 * @param {object[]} sources - array of data source objects
		 * @param {string} source.assetId - assetId of the source
		 * @param {string} source.type - type of source (e.g. 'package'/'module'/'uploadedFile'/etc)
		 * @param {string} capabilityName - if not provided, capability is determined based on the application name
		 * @returns {Promise<boolean>} promise that resolves true if all sources in the spec have authoring capability, false otherwise; and rejects for unexpected errors
		 */
		hasCapability(sources, capabilityName = null) {
			if (!sources || !sources.length) {
				return Promise.resolve(true);
			}
			try {
				if (!capabilityName) {
					capabilityName = this.getCurrentAuthoringCapability();
					if (!capabilityName) {
						const applicationName = this._getApplicationName();
						throw new Error(SAFETY_CHECK_ERRORS.unsupportedApplication + applicationName);
					}
				}
				const capabilityIndex = this._getCapabilityIndex(capabilityName);
				const sourcesToFetch = [];
				let hasCapability = true;

				for (let i = 0; i < sources.length; i++) {
					if (ASSET_TYPES_SUPPORT_CAPABILITIES.includes (sources[i].type)) {
						const assetId = sources[i].assetId;
						if (!assetId) {
							throw new Error(SAFETY_CHECK_ERRORS['missingAssetId'] + i);
						}
						const capability = this._getCapabilityFromCache(assetId);
						if (capability) {
							if (!capability[capabilityIndex]) {
								hasCapability = false;
								break;
							}
						} else {
							sourcesToFetch.push(assetId);
						}
					}
				}
				if (!hasCapability) {
					// has deny capability from cache, do not need to continue
					return Promise.resolve(false);
				}
				if (!sourcesToFetch.length) {
					return Promise.resolve(true);
				}
				return this._fetchCapabilitiesFromCM(sourcesToFetch, capabilityIndex);

			} catch(error) {
				return Promise.reject(error);
			}
		}

		_getCapabilityIndex(capabilityName) {
			let index = CAPABILITY_NAME_INDEX_MAP[capabilityName];
			if (index === undefined) {
				throw new Error(SAFETY_CHECK_ERRORS['unsupportedCapability'] + capabilityName);
			}
			return index;
		}

		_getCapabilityFromCache(assetId) {
			return this._capabilitiesCache[assetId];
		}

		_fetchCapabilitiesFromCM(assetIds, capabilityIndex) {
			const promises = [];
			assetIds.forEach(assetId => {
				if (!assetId) {
					throw new Error(SAFETY_CHECK_ERRORS['missingAssetId'] + assetIds.toString());
				}
				promises.push(this._getCapabilitiesPromise(assetId));
			});
			const notAvailableSources = [];
			return Promise.all(promises)
				.then((results) => {
					let hasCapability = true;
					for (let i = 0; i < results.length; i++) {
						const { data } = results[i].data;
						if (!data || !data.length) {
							notAvailableSources.push(assetIds[i]);
						} else {
							const capabilityValues = this._getCapabilityValuesFromCMResult(data[0], assetIds[i]);
							if (!capabilityValues[capabilityIndex]) {
								// we could break the loop here but as we already get the capabilities from CM, let's store all infos in the cache before reject
								hasCapability = false;
							}
						}
					}
					if (notAvailableSources.length) {
						this._glassContext.getCoreSvc('.Logger').warn(SAFETY_CHECK_ERRORS['notAvailableCapability'] + notAvailableSources.toString());
					}
					return Promise.resolve(hasCapability);
				});
		}

		_getCapabilityValuesFromCMResult(data, assetId) {
			const capabilitiesResult = data.effectiveUserCapabilities;
			const capabilityValues = [];
			capabilityValues.push(!!capabilitiesResult.includes(AUTHOR_DASHBOARD_CAPABILITY));
			this._addCapabilityValues(assetId, capabilityValues);
			return capabilityValues;
		}

		_getCapabilitiesPromise(assetId) {
			return this._ajaxService
				.ajax({
					url: 'v1/objects/' + assetId + '?fields=effectiveUserCapabilities',
					type: 'GET',
					headers: {
						'Accept': 'application/json'
					}
				});
		}

	}
	return CapabilitiesService;
});
